/**
 * Local (IndexedDB) persistence of the drawings library, plus change
 * notifications across components and browser tabs.
 *
 * Three separate stores are used so listing drawings doesn't need to load
 * every scene, and binary files (images) are shared and deduplicated.
 */

import {
  clear,
  createStore,
  del,
  delMany,
  get,
  getMany,
  keys,
  set,
  setMany,
  update,
  values,
} from "idb-keyval";

import type { BinaryFileData } from "@excalidraw/excalidraw/types";

import { STORAGE_KEYS } from "../app_constants";

import type {
  DrawingId,
  DrawingMeta,
  DrawingScene,
  Folder,
  FolderId,
  LibraryChangeEvent,
  ServerDrawing,
  LibraryChangeOrigin,
} from "./types";

const metaStore = createStore("excalidraw-drawings-meta", "meta");
const sceneStore = createStore("excalidraw-drawings-scenes", "scenes");
const fileStore = createStore("excalidraw-drawings-files", "files");
const folderStore = createStore("excalidraw-drawings-folders", "folders");
/** server copies of drawings in conflict, kept until the user resolves */
const conflictStore = createStore("excalidraw-drawings-conflicts", "conflicts");
/** IndexedDB store used by upstream LocalData for the images of the scene
 * in the editor */
export const editorFilesStore = createStore("files-db", "files-store");
/** localStorage key of the drawing whose scene is in localStorage (upstream
 * storage, see editor.ts) */
export const STORED_DRAWING_KEY = "excalidraw-library-current";
/** changes not synced yet of a user who is no longer the one logged in on
 * this browser, kept until they log in here again (key: user; "" for a local
 * copy made before libraries were per-user, see sync.ts) */
const stashStore = createStore("excalidraw-drawings-stash", "stash");

type PendingStash = {
  savedAt: number;
  metas: DrawingMeta[];
  scenes: [DrawingId, DrawingScene][];
  folders: Folder[];
  conflicts: ServerDrawing[];
  files: BinaryFileData[];
};

/** `newer` replaces the items of `older` with the same id */
const mergeById = <T extends { id: string }>(older: T[], newer: T[]) => [
  ...older.filter((item) => !newer.some((other) => other.id === item.id)),
  ...newer,
];

export const DrawingStore = {
  listMeta: () => values<DrawingMeta>(metaStore),

  getMeta: (id: DrawingId) => get<DrawingMeta>(id, metaStore),

  /** read-modify-write in a single transaction; returning `undefined` from
   * the updater leaves the record untouched */
  updateMeta: async (
    id: DrawingId,
    updater: (prev: DrawingMeta | undefined) => DrawingMeta | undefined,
  ) => {
    let result: DrawingMeta | undefined;
    await update<DrawingMeta | undefined>(
      id,
      (prev) => {
        result = updater(prev);
        return result ?? prev;
      },
      metaStore,
    );
    return result;
  },

  getScene: (id: DrawingId) => get<DrawingScene>(id, sceneStore),

  putScene: (id: DrawingId, scene: DrawingScene) => set(id, scene, sceneStore),

  remove: async (id: DrawingId) => {
    await del(id, sceneStore);
    await del(id, conflictStore);
    await del(id, metaStore);
  },

  listFolders: () => values<Folder>(folderStore),

  getFolder: (id: FolderId) => get<Folder>(id, folderStore),

  putFolder: (folder: Folder) => set(folder.id, folder, folderStore),

  getConflictCopy: (id: DrawingId) => get<ServerDrawing>(id, conflictStore),

  putConflictCopy: (drawing: ServerDrawing) =>
    set(drawing.id, drawing, conflictStore),

  deleteConflictCopy: (id: DrawingId) => del(id, conflictStore),

  getFiles: async (ids: readonly string[]) => {
    if (!ids.length) {
      return [];
    }
    const files = await getMany<BinaryFileData | undefined>(
      ids as string[],
      fileStore,
    );
    return files.filter((file): file is BinaryFileData => !!file);
  },

  getMissingFileIds: async (ids: readonly string[]) => {
    const existing = new Set((await keys(fileStore)) as string[]);
    return ids.filter((id) => !existing.has(id));
  },

  putFiles: (files: readonly BinaryFileData[]) =>
    files.length
      ? setMany(
          files.map((file) => [file.id, file]),
          fileStore,
        )
      : Promise.resolve(),

  /** puts aside the drawings and folders with changes not synced yet (or in
   * conflict) of `user`, with their scenes and images: clearAll() would
   * lose them. Returns how many drawings. */
  stashPending: async (user: string) => {
    const metas = (await DrawingStore.listMeta()).filter(
      (meta) => meta.dirty || meta.conflict,
    );
    const folders = (await DrawingStore.listFolders()).filter(
      (folder) => folder.dirty,
    );
    if (!metas.length && !folders.length) {
      return 0;
    }
    const ids = metas.map((meta) => meta.id);
    const scenes = (
      await getMany<DrawingScene | undefined>(ids, sceneStore)
    ).flatMap((scene, index) =>
      scene ? [[ids[index], scene] as [DrawingId, DrawingScene]] : [],
    );
    const conflicts = (
      await getMany<ServerDrawing | undefined>(ids, conflictStore)
    ).filter((copy): copy is ServerDrawing => !!copy);
    const fileIds = new Set<string>();
    for (const item of [...metas, ...conflicts]) {
      item.fileIds.forEach((id) => fileIds.add(id));
    }
    const files = await DrawingStore.getFiles([...fileIds]);
    // e.g. stashed earlier, and the user hasn't logged in here since
    const previous = await get<PendingStash>(user, stashStore);
    const stash: PendingStash = {
      savedAt: Date.now(),
      metas: mergeById(previous?.metas ?? [], metas),
      scenes: [
        ...(previous?.scenes ?? []).filter(([id]) => !ids.includes(id)),
        ...scenes,
      ],
      folders: mergeById(previous?.folders ?? [], folders),
      conflicts: mergeById(previous?.conflicts ?? [], conflicts),
      files: mergeById(previous?.files ?? [], files),
    };
    await set(user, stash, stashStore);
    return metas.length;
  },

  /** puts back what stashPending() put aside for `user`, to be synced.
   * Returns how many drawings. */
  restorePending: async (user: string) => {
    const stash = await get<PendingStash>(user, stashStore);
    if (!stash) {
      return 0;
    }
    await DrawingStore.putFiles(stash.files);
    await setMany(stash.scenes, sceneStore);
    await setMany(
      stash.conflicts.map((copy) => [copy.id, copy]),
      conflictStore,
    );
    await setMany(
      stash.folders.map((folder) => [folder.id, folder]),
      folderStore,
    );
    await setMany(
      stash.metas.map((meta) => [meta.id, meta]),
      metaStore,
    );
    await del(user, stashStore);
    return stash.metas.length;
  },

  /** empties the local library and the scene of the editor (e.g. another
   * user logged in on this browser); changes put aside by stashPending()
   * are kept */
  clearAll: async () => {
    await Promise.all(
      [
        metaStore,
        sceneStore,
        fileStore,
        folderStore,
        conflictStore,
        editorFilesStore,
      ].map((store) => clear(store)),
    );
    try {
      localStorage.removeItem(STORED_DRAWING_KEY);
      localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
    } catch (error) {
      console.error(error);
    }
  },

  /** delete files not referenced by any drawing anymore */
  collectGarbageFiles: async () => {
    const referenced = new Set<string>();
    for (const meta of await DrawingStore.listMeta()) {
      meta.fileIds.forEach((id) => referenced.add(id));
    }
    for (const copy of await values<ServerDrawing>(conflictStore)) {
      copy.fileIds.forEach((id) => referenced.add(id));
    }
    const orphans = ((await keys(fileStore)) as string[]).filter(
      (id) => !referenced.has(id),
    );
    if (orphans.length) {
      await delMany(orphans, fileStore);
    }
  },
};

// -----------------------------------------------------------------------------
// change notifications
// -----------------------------------------------------------------------------

export const TAB_ID = Math.random().toString(36).slice(2);

type Listener = (event: LibraryChangeEvent) => void;
const listeners = new Set<Listener>();

const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel("excalidraw-drawings")
    : null;

channel?.addEventListener("message", (event: MessageEvent) => {
  listeners.forEach((listener) => listener(event.data as LibraryChangeEvent));
});

export const emitLibraryChange = (
  ids: DrawingId[],
  origin: LibraryChangeOrigin,
) => {
  if (!ids.length) {
    return;
  }
  const event: LibraryChangeEvent = { ids, origin, tabId: TAB_ID };
  listeners.forEach((listener) => listener(event));
  channel?.postMessage(event);
};

export const onLibraryChange = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
